import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, repeat, shareReplay, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private http: HttpClient,
    public authenticationService: AuthenticationService,
    private messageService: MessageService
  ) {}

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  getAPI<Type>(
    endpoint: string,
    queryParams?: any,
    pollInterval?: number
  ): Observable<any> {
    let parameters = {};
    if (pollInterval) {
      if (queryParams) {
        parameters = queryParams;
      }
      return this.http
        .get<Type>(`${environment.apiUrl}/${endpoint}`, {
          observe: 'response',
          responseType: 'json',
          params: parameters,
        })
        .pipe(
          catchError(this.handleError),
          // shareReplay(),
          repeat({ delay: pollInterval })
          // repeat({ delay: pollInterval, count: 1 })
        );
    } else {
      if (queryParams) {
        parameters = queryParams;
        // //// console.log('parameters', parameters);
      }
      return this.http
        .get<Type>(`${environment.apiUrl}/${endpoint}`, {
          observe: 'response',
          responseType: 'json',
          params: parameters,
        })
        .pipe(catchError(this.handleError), shareReplay(1));
    }
  }

  updateAPI<Type>(
    endpoint: string,
    query_id: number,
    data: any
  ): Observable<any> {
    return this.http
      .put<Type>(`${environment.apiUrl}/${endpoint}/${query_id}`, data, {
        observe: 'response',
        responseType: 'json',
      })
      .pipe(catchError(this.handleError));
  }

  patchAPI<Type>(
    endpoint: string,
    data: any,
    query_id?: number,
  ): Observable<any> {
    const url = query_id
      ? `${environment.apiUrl}/${endpoint}/${query_id}`
      : `${environment.apiUrl}/${endpoint}`;
    return this.http
      .patch<Type>(url, data, {
        observe: 'response',
        responseType: 'json',
      })
      .pipe(catchError(this.handleError));
  }

  postAPI<Type>(endpoint: string, data: any): Observable<any> {
    return this.http
      .post<Type>(`${environment.apiUrl}/${endpoint}`, data, {
        observe: 'response',
        responseType: 'json',
      })
      .pipe(catchError(this.handleError));
  }

  deleteAPI<Type>(endpoint: string, query_id: Number): Observable<any> {
    return this.http
      .delete<Type>(`${environment.apiUrl}/${endpoint}/${query_id}`, {
        observe: 'response',
        responseType: 'json',
      })
      .pipe(catchError(this.handleError));
  }

  // When something breaks (most commonly an expired session), several
  // requests in flight at once all fail with the identical message and each
  // independently calls handleError — without this guard that shows the
  // same toast once per request. Only suppress genuine back-to-back repeats
  // of the *same* message within a short window; a different error, or the
  // same one recurring later, still shows normally.
  private lastErrorMessage: string | null = null;
  private lastErrorAt = 0;
  private readonly errorToastDedupWindowMs = 4000;

  handleError = (error: any) => {
    // HttpErrorResponses are already translated into a user-facing message
    // by ErrorInterceptor before they reach this service.
    const errorMessage =
      typeof error === 'string' ? error : 'An unexpected error occurred.';

    const now = Date.now();
    const isRepeat =
      this.lastErrorMessage === errorMessage &&
      now - this.lastErrorAt < this.errorToastDedupWindowMs;

    if (!isRepeat) {
      this.messageService.add({
        severity: 'error',
        summary: 'Something went wrong',
        detail: errorMessage,
      });
    }

    this.lastErrorMessage = errorMessage;
    this.lastErrorAt = now;

    return throwError(() => {
      return errorMessage;
    });
  };

  getFileAPI<Type>(endpoint: string, params?: any): Observable<any> {
    return this.http
      .get(`${environment.apiUrl}/${endpoint}`, {
        observe: 'response',
        responseType: 'blob',
        params: params || {},
      })
      .pipe(catchError(this.handleError));
  }
}
